import React, { useContext } from 'react';
import { Box, Paper, Typography, Button, Stack } from '@mui/material';
import { Link } from 'react-router-dom';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { AuthContext, MyContext } from '../context/AuthContext';

/**
 * LOW WALLET BANNER
 *
 * Surfaces above the composer when the user's wallet is under 1 hour.
 * Offers three clear paths out of the low-wallet state:
 *  - Vote on posts (most organic — earn time by engaging)
 *  - Store (buy more time — revenue)
 *  - Daily check-in (already has its own banner; we just nudge)
 *
 * Hidden when wallet is empty completely (the navbar wallet chip already
 * flashes red in that state) and when wallet is healthy.
 */

const LOW_THRESHOLD_SECONDS = 3600;        // under 1 hour = low
const CRITICAL_THRESHOLD_SECONDS = 600;    // under 10 min = critical

function formatShort(seconds: number): string {
  if (seconds <= 0) return '0';
  const m = Math.floor(seconds / 60);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}

function LowWalletBanner() {
  const userRecord = useContext<MyContext>(AuthContext).userRecord;
  if (!userRecord) return null;

  const wallet = userRecord.time_wallet_seconds ?? 0;
  if (wallet >= LOW_THRESHOLD_SECONDS) return null;

  const critical = wallet < CRITICAL_THRESHOLD_SECONDS;
  const accent = critical ? '#b51e1b' : '#ff9800';

  return (
    <Paper
      elevation={0}
      variant="outlined"
      sx={{
        mb: 2,
        p: 1.5,
        borderRadius: 2,
        borderLeft: '4px solid',
        borderLeftColor: accent,
        bgcolor: (t) =>
          t.palette.mode === 'dark'
            ? `${accent}14`
            : `${accent}0A`,
      }}
    >
      <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={1.5}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
          <WarningAmberIcon sx={{ color: accent, width: 22, height: 22, flexShrink: 0 }} />
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="body2"
              sx={{
                fontFamily: '"VT323", monospace',
                letterSpacing: 1,
                fontSize: '1rem',
                color: accent,
                fontWeight: 700,
              }}
            >
              {critical ? 'WALLET CRITICAL' : 'WALLET LOW'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {formatShort(wallet)} left. Earn more by voting on posts, or top up in the store.
            </Typography>
          </Box>
        </Box>
        <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
          <Button
            component={Link}
            to="/store"
            size="small"
            variant="contained"
            sx={{
              textTransform: 'none',
              bgcolor: accent,
              '&:hover': { bgcolor: accent, filter: 'brightness(0.9)' },
              fontWeight: 700,
            }}
          >
            Top up
          </Button>
          <Button
            component={Link}
            to="/leaderboard"
            size="small"
            variant="outlined"
            sx={{ textTransform: 'none' }}
          >
            Vote on posts
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
}

export default LowWalletBanner;
